import { computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  catchError,
  concat,
  debounceTime,
  EMPTY,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
} from 'rxjs';

/** The part of a backend `PageModel*` the paging reads. */
export interface AutocompletePage<T> {
  content?: T[];
  page?: { last?: boolean; elements?: number };
}

export type AutocompletePageFetcher<T> = (
  search: string,
  page: number,
  size: number,
) => Observable<AutocompletePage<T>>;

export interface AutocompletePagingOptions {
  pageSize?: number;
  debounceMs?: number;
}

export interface AutocompletePaging<T> {
  readonly items: Signal<T[]>;
  readonly loadingMore: Signal<boolean>;
  /** A search has answered and found nothing — not merely "no items yet". */
  readonly isEmptyResult: Signal<boolean>;
  /** The search the current `items` answer, `null` before the first answer. */
  readonly answeredSearch: Signal<string | null>;
  /** How many items the answered search matches across all pages, `null` if the backend doesn't say. */
  readonly total: Signal<number | null>;
  /** Wire to `fr8bot-ui-autocomplete-field` `(autocompleteSearch)`. */
  search(text: string): void;
  /** Wire to `fr8bot-ui-autocomplete-field` `(autocompleteScrollEnd)`. */
  loadMore(): void;
}

interface PagingState<T> {
  answeredSearch: string | null;
  items: T[];
  total: number | null;
  hasMore: boolean;
  loadingMore: boolean;
}

const INITIAL_STATE: PagingState<never> = {
  answeredSearch: null,
  items: [],
  total: null,
  hasMore: false,
  loadingMore: false,
};

/**
 * Search-as-you-type with infinite scroll for an autocomplete. A new search restarts from the
 * first page (latest wins); `loadMore` fetches the next page, one at a time, until the backend
 * reports the last one. Call in an injection context.
 */
export function injectAutocompletePaging<T>(
  fetchPage: AutocompletePageFetcher<T>,
  { pageSize = 20, debounceMs = 200 }: AutocompletePagingOptions = {},
): AutocompletePaging<T> {
  const search$ = new Subject<string>();
  const loadMore$ = new Subject<void>();

  // `loadMore$` is only listened to between pages, so calls while a page is in flight are dropped.
  const loadFrom = (
    search: string,
    page: number,
    loaded: T[],
    total: number | null = null,
  ): Observable<PagingState<T>> =>
    fetchPage(search, page, pageSize).pipe(
      map((response): PagingState<T> => {
        const content = response.content ?? [];
        const last = response.page?.last ?? content.length < pageSize;
        return {
          answeredSearch: search,
          items: [...loaded, ...content],
          total: response.page?.elements ?? total,
          hasMore: !last,
          loadingMore: false,
        };
      }),
      catchError(() =>
        of<PagingState<T>>({
          answeredSearch: search,
          items: loaded,
          total,
          hasMore: false,
          loadingMore: false,
        }),
      ),
      switchMap((state) =>
        concat(
          of(state),
          state.hasMore
            ? loadMore$.pipe(
                take(1),
                switchMap(() =>
                  concat(
                    of({ ...state, loadingMore: true }),
                    loadFrom(search, page + 1, state.items, state.total),
                  ),
                ),
              )
            : EMPTY,
        ),
      ),
    );

  const state = toSignal(
    search$.pipe(
      debounceTime(debounceMs),
      switchMap((search) => loadFrom(search, 0, [])),
    ),
    { initialValue: INITIAL_STATE as PagingState<T> },
  );

  return {
    items: computed(() => state().items),
    loadingMore: computed(() => state().loadingMore),
    isEmptyResult: computed(() => state().answeredSearch !== null && state().items.length === 0),
    answeredSearch: computed(() => state().answeredSearch),
    total: computed(() => state().total),
    search: (text) => search$.next(text),
    loadMore: () => loadMore$.next(),
  };
}
